import { useEffect, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hourglass, FileWarning, ReceiptText, RefreshCw, Download, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";

const CATS = [
  { key: "stale_open", label: "Open · not updated", icon: Hourglass, tone: "amber" },
  { key: "completed_uninvoiced", label: "Completed · not invoiced", icon: FileWarning, tone: "rose" },
  { key: "processed_uninvoiced", label: "Processed · not invoiced", icon: ReceiptText, tone: "violet" },
];

const TONE = {
  amber: { card: "border-amber-200 bg-amber-50/50", active: "ring-2 ring-amber-400", text: "text-amber-800" },
  rose: { card: "border-rose-200 bg-rose-50/50", active: "ring-2 ring-rose-400", text: "text-rose-800" },
  violet: { card: "border-violet-200 bg-violet-50/50", active: "ring-2 ring-violet-400", text: "text-violet-800" },
};

export function AgedWorkPanel() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [active, setActive] = useState("stale_open");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const { can } = usePermissions();

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/operations/aged-work/export?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aged-work-${days}d.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Aged work report downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const emailReport = async () => {
    if (!window.confirm(`Email the ${days}-day aged work report?\n\nEach BU manager gets their own BU's list; operations management and executives get the full report.`)) return;
    setEmailing(true);
    try {
      const { data: r } = await api.post(`/operations/aged-work/send-report?days=${days}`);
      toast.success(`Sent ${r.emails_sent} email(s) — ${r.bu_reports} BU report(s), ${r.total_aged} aged jobs`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setEmailing(false); }
  };

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await api.get(`/operations/aged-work?days=${d}&limit=200`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load aged work report");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cat = data?.categories?.[active];

  return (
    <div className="space-y-4" data-testid="ops-aged-work-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Aged threshold</span>
        <Select value={days} onValueChange={(v) => { setDays(v); load(v); }}>
          <SelectTrigger data-testid="aged-work-days-select" className="w-[130px] h-8 bg-white text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8" onClick={() => load()} disabled={loading} data-testid="aged-work-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" variant="outline" className="h-8" onClick={exportExcel} disabled={exporting} data-testid="aged-work-export-btn">
          {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Export Excel
        </Button>
        {can("operations", "edit") && (
          <Button size="sm" className="h-8 bg-gbe hover:bg-gbe/90 text-white" onClick={emailReport} disabled={emailing} data-testid="aged-work-email-btn">
            {emailing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Email to BU managers
          </Button>
        )}
        <span className="ml-auto text-[11px] text-slate-400">Lists capped at 200 oldest jobs per category</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {CATS.map(({ key, label, icon: Icon, tone }) => {
          const t = TONE[tone];
          const c = data?.categories?.[key];
          return (
            <button key={key} onClick={() => setActive(key)} data-testid={`aged-work-card-${key}`}
                    className={`rounded-lg border ${t.card} p-4 text-left transition-shadow ${active === key ? t.active : "hover:shadow-sm"}`}>
              <div className={`flex items-center gap-2 ${t.text}`}>
                <Icon className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-medium">{label} &gt; {data?.days ?? days}d</span>
              </div>
              <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">
                {loading ? "…" : fmtNumber(c?.total ?? 0)}
              </div>
            </button>
          );
        })}
      </div>

      {cat && (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            <BreakdownCard title="By status" rows={cat.by_status} keyField="status" testid="aged-work-by-status" />
            <BreakdownCard title="By business unit" rows={cat.by_bu} keyField="business_unit" testid="aged-work-by-bu" />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">{cat.label}</span>
              <span className="ml-auto text-[11px] text-slate-500">
                Showing {cat.items?.length ?? 0} of {fmtNumber(cat.total ?? 0)} (oldest first)
              </span>
            </div>
            {(cat.items || []).length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center" data-testid="aged-work-empty">
                Nothing in this category — no aged work found. 🎉
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="aged-work-items-table">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-medium px-3 py-2">Job #</th>
                      <th className="text-left font-medium px-3 py-2">Title</th>
                      <th className="text-left font-medium px-3 py-2">Client</th>
                      <th className="text-left font-medium px-3 py-2">BU</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                      <th className="text-left font-medium px-3 py-2">Last activity</th>
                      <th className="text-right font-medium px-3 py-2">Aged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.items.map((it) => (
                      <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{it.job_number || "—"}</td>
                        <td className="px-3 py-2 text-slate-800 max-w-md truncate">{it.title || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">{it.client_name || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{it.business_unit || "Unassigned"}</td>
                        <td className="px-3 py-2">
                          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">
                            {it.status}{it.substatus ? ` · ${it.substatus}` : ""}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{(it.ref_date || "").slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`text-xs font-medium ${it.days_aged > 90 ? "text-rose-700" : "text-amber-700"}`}>
                            {it.days_aged != null ? `${fmtNumber(it.days_aged)}d` : "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function BreakdownCard({ title, rows, keyField, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">{title}</div>
      {(rows || []).length === 0 ? (
        <div className="text-xs text-slate-400">No data</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {rows.map((r) => (
            <span key={r[keyField]} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-700">
              {r[keyField]}
              <span className="font-semibold text-slate-900">{fmtNumber(r.n)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
